import { persist } from 'zustand/middleware';
import { create } from 'zustand';
import { GalleryView } from '@/types';

export const MIN_ASSET_TILE_SIZE = 140;
export const MAX_ASSET_TILE_SIZE = 300;
export const ASSET_TILE_ZOOM_STEP = 40;
export const DEFAULT_THUMBNAIL_SIZE = 200;

const clampTileSize = (size: number) => Math.min(MAX_ASSET_TILE_SIZE, Math.max(MIN_ASSET_TILE_SIZE, size));

interface UserSettingsStore {
  showInspector: boolean;
  setShowInspector: (value: boolean) => void;
  showUploadInspector: boolean;
  setShowUploadInspector: (value: boolean) => void;
  galleryView: GalleryView;
  setGalleryView: (value: GalleryView) => void;
  collapseSideNavigation: boolean;
  setCollapseSideNavigation: (value: boolean) => void;
  showFocalPoint: boolean;
  toggleFocalPoint: () => void;
  showFoldersAndAlbums: boolean;
  setShowFoldersAndAlbums: (value: boolean) => void;
  thumbnailSize: number;
  setThumbnailSize: (value: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  includeSubFolderAssets: boolean;
  setIncludeSubFolderAssets: (value: boolean) => void;
}

const DEFAULTS = {
  showInspector: true,
  showUploadInspector: true,
  galleryView: GalleryView.TILE,
  collapseSideNavigation: true,
  showFocalPoint: false,
  showFoldersAndAlbums: true,
  thumbnailSize: DEFAULT_THUMBNAIL_SIZE,
  includeSubFolderAssets: false,
};

type PersistedSettings = Pick<UserSettingsStore, keyof typeof DEFAULTS>;

const isGalleryView = (value: unknown): value is GalleryView =>
  value === GalleryView.LIST || value === GalleryView.TILE;

/** Only the boolean depends on `galleryView`/`thumbnailSize`, so a zoom step that doesn't cross the disabled threshold re-renders neither zoom control. */
export const selectIsZoomInDisabled = (state: UserSettingsStore) =>
  state.galleryView === GalleryView.LIST || state.thumbnailSize >= MAX_ASSET_TILE_SIZE;
export const selectIsZoomOutDisabled = (state: UserSettingsStore) =>
  state.galleryView === GalleryView.LIST || state.thumbnailSize <= MIN_ASSET_TILE_SIZE;

export const useUserSettingsStore = create<UserSettingsStore>()(
  persist(
    (set, get) => ({
      ...DEFAULTS,

      setShowInspector: (value) => set({ showInspector: value }),
      setShowUploadInspector: (value) => set({ showUploadInspector: value }),
      setGalleryView: (value) => set({ galleryView: value }),
      setCollapseSideNavigation: (value) => set({ collapseSideNavigation: value }),

      toggleFocalPoint: () => set({ showFocalPoint: !get().showFocalPoint }),

      setShowFoldersAndAlbums: (value) => set({ showFoldersAndAlbums: value }),

      /**
       * Clamped here rather than at the call sites: before DM-1412 the
       * Math.min/Math.max pair was duplicated across four zoom controls, and
       * an unclamped value would be written straight into persisted state.
       */
      setThumbnailSize: (value) => set({ thumbnailSize: clampTileSize(value) }),
      zoomIn: () => set({ thumbnailSize: clampTileSize(get().thumbnailSize + ASSET_TILE_ZOOM_STEP) }),
      zoomOut: () => set({ thumbnailSize: clampTileSize(get().thumbnailSize - ASSET_TILE_ZOOM_STEP) }),

      setIncludeSubFolderAssets: (value) => set({ includeSubFolderAssets: value }),
    }),
    {
      name: 'user-settings',
      /**
       * No `version`/`migrate`: the pre-DM-1412 per-setting keys (`galleryView`,
       * `thumbnailSize`, …) are deliberately orphaned. These are cosmetic view
       * toggles, so a one-time reset costs a user one re-toggle — see DM-1412
       * AC 5 and the design spec's "Deviations" section.
       *
       * Typed as `Pick<UserSettingsStore, keyof typeof DEFAULTS>` rather than inferred, so a
       * setting added to `DEFAULTS` without a matching line here fails to compile instead of
       * silently never persisting.
       */
      partialize: (state): PersistedSettings => ({
        showInspector: state.showInspector,
        showUploadInspector: state.showUploadInspector,
        galleryView: state.galleryView,
        collapseSideNavigation: state.collapseSideNavigation,
        showFocalPoint: state.showFocalPoint,
        showFoldersAndAlbums: state.showFoldersAndAlbums,
        thumbnailSize: state.thumbnailSize,
        includeSubFolderAssets: state.includeSubFolderAssets,
      }),
      /**
       * Validates what localStorage hands back before it reaches memory. A stored `thumbnailSize`
       * outside the clamp bounds (hand-edited, or from before the bounds changed) would otherwise
       * render tiles off screen until the next zoom click; an unrecognized `galleryView` string
       * would flow straight into JSX expecting the enum.
       */
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PersistedSettings> | undefined;
        const persistedGalleryView = persisted?.galleryView;
        return {
          ...currentState,
          ...persisted,
          thumbnailSize: clampTileSize(persisted?.thumbnailSize ?? currentState.thumbnailSize),
          galleryView: isGalleryView(persistedGalleryView) ? persistedGalleryView : currentState.galleryView,
        };
      },
    },
  ),
);

/**
 * Test helper. `persist` hydrates once at import and writes on every `set`, so
 * without this a settings change leaks into the next test in the file.
 */
export const resetUserSettingsStore = () => {
  useUserSettingsStore.setState(DEFAULTS);
  useUserSettingsStore.persist.clearStorage();
};
